import * as React from 'react';

import { PriceChangePlain } from '@blockworks/ui/legacy/labels';
import { cn } from '@blockworks/ui/utils';
import { priceFormatter } from '@blockworks/platform/utils';
import type { Ohclv24h } from '@blockworks/platform/api/research/assets';

type PriceInfoBlockProps = {
    price: number;
    currentPrice: number;
    openPrice: number;
    openPriceBtc: number;
    ohlcv24h: Ohclv24h;
    isBtc: boolean;
    className?: string;
};

const formatPrice = (price: number, isBtc: boolean) => {
    const maxNumDigit = price < 0.1 ? 3 : 10;
    const currency = isBtc ? 'BTC' : 'USD';
    const locale = isBtc ? 'de' : 'en';

    const maximumSignificantDigits = Math.min(maxNumDigit, Math.floor(Math.abs(Math.log(price)) * Math.LOG10E) + 3);

    const numberFormatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        maximumSignificantDigits,
    });

    return numberFormatter.format(price).replace('BTC', '₿');
};

const PriceInfoBlock = (props: PriceInfoBlockProps) => {
    const { price, currentPrice, ohlcv24h, isBtc = false, openPrice, openPriceBtc } = props;

    // Calculate 24h low/high barometer width
    const barometerWidthPercentage = Math.round(((currentPrice - ohlcv24h.low) / (ohlcv24h.high - ohlcv24h.low)) * 100);

    // Calculate change
    const change = isBtc ? ((price - openPriceBtc) / price) * 100 : ((price - openPrice) / price) * 100;
    const priceChange = price - (isBtc ? openPriceBtc : openPrice);

    return (
        <div>
            <div className="flex flex-wrap lg:flex-nowrap justify-start items-center self-stretch gap-2 lg:gap-8">
                <div className="flex flex-col justify-center items-start gap-1">
                    <div className="flex justify-start items-start relative gap-2">
                        <p className="w-64 text-4xl text-left text-dark">{formatPrice(price, isBtc)}</p>
                    </div>
                    <div className="flex justify-start items-center relative gap-1">
                        <p className={cn('text-base text-left', priceChange < 0 ? 'text-red-900' : 'text-green-900')}>
                            {priceFormatter(priceChange)}
                        </p>
                        <p className={cn('flex text-base text-left', change < 0 ? 'text-red-900' : 'text-green-900')}>
                            (<PriceChangePlain priceChangePercent={change} />)
                        </p>
                    </div>
                </div>
                <div className="flex flex-col justify-end items-start flex-grow relative gap-2 mb-2">
                    <div className="flex justify-between items-center self-stretch flex-grow-0 flex-shrink-0">
                        <div className="flex flex-col justify-start items-start relative gap-1">
                            <p className="text-xs text-left uppercase text-light-gray">24h low</p>
                            <p className="text-base text-left text-dark">{priceFormatter(ohlcv24h.low)}</p>
                        </div>
                        <div className="flex flex-col justify-start items-end relative gap-1">
                            <p className="text-xs text-left uppercase text-light-gray">24h high</p>
                            <p className="text-base text-left text-dark">{priceFormatter(ohlcv24h.high)}</p>
                        </div>
                    </div>
                    <div className="w-full relative">
                        <div className="bg-gray-50 w-full absolute h-4 flex">
                            <div
                                className="absolute bg-primary h-4"
                                style={{ width: `${barometerWidthPercentage}%` }}
                            />
                        </div>
                        <svg
                            className="absolute l-0 h-4 w-4 top-5 -ml-2"
                            width="15"
                            height="12"
                            viewBox="0 0 15 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            style={{ left: `${barometerWidthPercentage}%` }}
                        >
                            <path d="M7.48599 0L14.7577 12H0.214355L7.48599 0Z" fill="#5637CD" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PriceInfoBlock;
